/**
 * Borrar credencial — solo owner, con confirmación y el Vault desbloqueado. Queda en la auditoría.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { errMsg, isVaultLockedError, withUnlockRetry } from '@/hooks/useVault';

export function DeleteSecretButton({ secretId, name, ensureUnlocked }: { secretId: string; name: string; ensureUnlocked: () => Promise<boolean> }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const del = trpc.vault.deleteSecret.useMutation({
    onSuccess: () => {
      utils.vault.listSecrets.invalidate();
      toast.success(t('vault.delete.done', 'Credencial eliminada'));
      navigate('/vault');
    },
    onError: (e) => { if (!isVaultLockedError(e)) toast.error(errMsg(e)); },
  });

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="text-destructive hover:text-destructive">
        <Trash2 className="mr-2 h-4 w-4" />
        {t('vault.delete.cta', 'Eliminar')}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vault.delete.title', '¿Eliminar "{{name}}"?', { name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('vault.delete.body', 'Se borran la credencial, su historial y todos los accesos. El registro de auditoría se conserva.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void withUnlockRetry(ensureUnlocked, () => del.mutateAsync({ secretId })).catch(() => undefined)} disabled={del.isPending}>
              {t('vault.delete.cta', 'Eliminar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
